import { useState } from "react";
import { X, Loader2, FileCode } from "lucide-react";
import api, { CATEGORIES } from "@/lib/api";
import { toast } from "sonner";

export default function HtmlImportDialog({ open, onClose, onCreated }) {
  const [html, setHtml] = useState("");
  const [category, setCategory] = useState("mindmaps");
  const [author, setAuthor] = useState("SummaryMaps Editorial");
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const reset = () => { setHtml(""); setCategory("mindmaps"); setAuthor("SummaryMaps Editorial"); };

  const upload = async () => {
    if (!html.trim()) { toast.error("Paste some HTML first."); return; }
    setBusy(true);
    try {
      // 1) Parse HTML to extract metadata
      const { data: parsed } = await api.post("/admin/articles/parse-html", { html });
      if (!parsed?.title || !parsed?.body) {
        toast.error("Couldn't find a title or body in that HTML.");
        setBusy(false);
        return;
      }
      // 2) Create the article (published immediately)
      const payload = {
        title: parsed.title,
        category,
        excerpt: parsed.excerpt || parsed.title,
        body: parsed.body,
        cover_image_url: parsed.cover_image_url || "https://images.unsplash.com/photo-1497633762265-9d179a990aa6?auto=format&fit=crop&w=1600&q=80",
        published: true,
        author,
      };
      const { data: created } = await api.post("/admin/articles", payload);
      toast.success(`Published "${created.title}"`);
      reset();
      onCreated?.(created);
      onClose?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Upload failed.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-start sm:items-center justify-center p-3 sm:p-4 overflow-y-auto" data-testid="html-import-dialog">
      <div className="bg-white rounded-2xl w-full max-w-2xl my-4 sm:my-8">
        <div className="px-5 sm:px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-2.5 min-w-0">
            <span className="w-9 h-9 rounded-xl bg-brand-soft text-brand flex items-center justify-center shrink-0">
              <FileCode className="w-4 h-4" />
            </span>
            <div className="min-w-0">
              <h2 className="font-serif text-lg text-ink truncate">Upload HTML</h2>
              <p className="text-xs text-gray-500">Paste HTML — title, cover and body are extracted automatically.</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-gray-100 text-gray-500" data-testid="close-import">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 sm:p-6 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs uppercase tracking-[0.15em] text-gray-500 mb-2">Category</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none bg-white text-sm"
                data-testid="import-category"
              >
                {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.15em] text-gray-500 mb-2">Author</label>
              <input
                value={author}
                onChange={(e) => setAuthor(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none text-sm"
                data-testid="import-author"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-[0.15em] text-gray-500 mb-2">HTML</label>
            <textarea
              value={html}
              onChange={(e) => setHtml(e.target.value)}
              rows={14}
              placeholder='<article><h1>Your title</h1><p>Body…</p></article>'
              className="w-full px-3 py-2.5 rounded-lg border border-gray-200 focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none font-mono text-xs leading-relaxed"
              data-testid="import-html-textarea"
            />
          </div>

          <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-100">
            <button onClick={onClose} className="px-4 py-2 rounded-lg text-gray-600 hover:bg-gray-100 text-sm" data-testid="import-cancel">Cancel</button>
            <button onClick={upload} disabled={busy} className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-brand text-white hover:bg-brand-hover text-sm font-medium disabled:opacity-60" data-testid="import-upload-btn">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}
              {busy ? "Uploading…" : "Upload & publish"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
